// filledMap — choropleth. Without GeoJSON polygons we render a point
// summary as a fallback (same code path as MapVisual). The backend can
// later supply a GeoJSON layer in data.meta.geojson and we'd switch to it.

import { MapContainer, TileLayer, CircleMarker, Tooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { ChartShell } from "./_chartShell";
import type { VisualProps } from "./types";
import type { DataRow } from "../types/pbi";
import { palette } from "../theme";

function pickLatLon(r: DataRow): [number, number] | null {
  const lat = r.latitude ?? r.lat ?? r.Latitude ?? r.LATITUDE;
  const lon = r.longitude ?? r.lon ?? r.lng ?? r.Longitude ?? r.LONGITUDE;
  if (typeof lat !== "number" || typeof lon !== "number") return null;
  return [lat, lon];
}

export function FilledMapVisual({ spec, data }: VisualProps) {
  const rows = data.rows ?? [];
  const points = rows
    .map((r) => ({ r, latlon: pickLatLon(r) }))
    .filter((p): p is { r: DataRow; latlon: [number, number] } => p.latlon !== null);
  const empty = points.length === 0;
  const center: [number, number] = empty
    ? [39.5, -98.35]
    : [
        points.reduce((s, p) => s + p.latlon[0], 0) / points.length,
        points.reduce((s, p) => s + p.latlon[1], 0) / points.length,
      ];

  return (
    <ChartShell title={spec.title} isEmpty={empty} status={data.status}>
      <MapContainer
        center={center}
        zoom={4}
        scrollWheelZoom={false}
        style={{ height: "100%", width: "100%", borderRadius: 4 }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {points.map((p, i) => (
          <CircleMarker
            key={i}
            center={p.latlon}
            radius={10}
            pathOptions={{
              color: palette[2],
              fillColor: palette[2],
              fillOpacity: 0.55,
            }}
          >
            <Tooltip>
              {Object.entries(p.r)
                .slice(0, 5)
                .map(([k, v]) => `${k}: ${v ?? ""}`)
                .join(" · ")}
            </Tooltip>
          </CircleMarker>
        ))}
      </MapContainer>
    </ChartShell>
  );
}
